import React, { useCallback, useMemo, useState } from 'react';

import { Typography } from '@mui/material';
import type { DataGridProProps } from '@mui/x-data-grid-pro';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { uniq } from 'ramda';
import type { OptionSchema } from 'c-components';
import { Autocomplete, AutoGrid, TextField } from 'c-components';
import { useCommonTranslation } from 'c-translation';
import type { PopDisplays_LogFileImportSummary } from 'c-sdk/Types/EntityTypes/PopDisplays/PopDisplays_LogFileImportSummary';

type Props = {
    resources: PopDisplays_LogFileImportSummary[];
    loading: boolean;
};

const initialState: DataGridProProps['initialState'] = {
    sorting: {
        sortModel: [{ field: 'item', sort: 'asc' }],
    },
};

const LogFileImportSummaryTable: React.FC<Props> = ({ resources, loading }) => {
    const uniqueTypeOptions = useMemo<OptionSchema[]>(
        () => uniq(resources.map(res => res.item)).map(item => ({ value: item, label: item })),
        [resources],
    );

    const [types, setTypes] = useState<string[]>([]);
    const [search, setSearch] = useState('');

    const visibleRes = useMemo(() => {
        const doSearchCheck = search?.trim()?.length > 0;
        const doTypesCheck = types?.length > 0;

        if (doSearchCheck || doTypesCheck) {
            return resources.filter(res => {
                let visible = true;

                if (doSearchCheck && res.name.toLowerCase().indexOf(search.toLowerCase()) === -1) {
                    visible = false;
                }
                if (doTypesCheck && types.indexOf(res.item) === -1) {
                    visible = false;
                }

                return visible;
            });
        }
        return resources;
    }, [resources, search, types]);

    const rows = useMemo(
        () => visibleRes.map((res, index) => ({ ...res, rowId: index })),
        [visibleRes],
    );

    const onSearchUpdated = useCallback(e => {
        setSearch(e.target.value);
    }, []);

    const t = useCommonTranslation();
    const columns = useMemo<DataGridProProps['columns']>(
        () => [
            {
                field: 'item',
                headerName: t('Modules.PopDisplays.LogFileResource.table.itemCol'),
                sortable: true,
            },
            {
                field: 'id',
                headerName: t('Modules.PopDisplays.LogFileResource.table.idCol'),
                sortable: true,
                type: 'number',
            },
            {
                field: 'name',
                headerName: t('Modules.PopDisplays.LogFileResource.table.nameCol'),
                flex: 1,
                sortable: true,
            },
            {
                field: 'extid',
                headerName: t('Modules.PopDisplays.LogFileResource.table.extIdCol'),
            },
            {
                field: 'reference',
                headerName: t('Modules.PopDisplays.LogFileResource.table.referenceCol'),
            },
            {
                field: 'count',
                headerName: t('Modules.PopDisplays.LogFileResource.table.playsCol'),
                sortable: true,
                type: 'number',
            },
            {
                field: 'impacts',
                headerName: t('Modules.PopDisplays.LogFileResource.table.impactsCol'),
                sortable: true,
                type: 'number',
            },
            {
                field: 'duration',
                headerName: t('Modules.PopDisplays.LogFileResource.table.durationCol'),
                sortable: true,
                type: 'number',
            },
        ],
        [t],
    );

    return (
        <>
            <Typography variant="h4">
                {useCommonTranslation(
                    'Modules.PopDisplays.LogFileImport.details.resourcesSectionTitle',
                )}{' '}
                ({visibleRes.length})
            </Typography>
            <AutoGrid sx={{ mb: 2 }} xs="auto" spacing={2}>
                <TextField
                    sx={{ mt: 0.5 }}
                    size="small"
                    variant="standard"
                    label={t('Modules.PopDisplays.LogFileImport.details.filterByNameLabel')}
                    value={search}
                    onChange={onSearchUpdated}
                />
                <Autocomplete
                    sx={{ minWidth: 200 }}
                    textFieldProps={{
                        label: useCommonTranslation(
                            'Modules.PopDisplays.LogFileImport.details.filterByTypeLabel',
                        ),
                        variant: 'standard',
                    }}
                    options={uniqueTypeOptions}
                    onChange={setTypes}
                    value={types}
                    multiple
                />
            </AutoGrid>
            <DataGridPro
                disableRowSelectionOnClick
                disableColumnReorder
                disableColumnSelector
                disableColumnPinning
                hideFooter
                loading={loading}
                initialState={initialState}
                columns={columns}
                rows={rows}
                getRowId={row => row.rowId}
                rowHeight={50}
            />
        </>
    );
};

export default LogFileImportSummaryTable;
